import React from 'react';
import { connect } from 'react-redux';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import { Announcement } from '../../../../redux/modules/types/announcements';
import { deleteAnnouncement, DELETE_ANNOUNCEMENT } from '../../../../redux/modules/announcements/admin';
import { selectIsLoadingEvery } from '../../../../redux/modules/ui/loaders';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '../../../../utils/i18n';
import notification from '../../../../utils/notification';

// Components
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';

type DispatchProps = {
  deleteAnnouncement: (announcementId: string) => Promise<void>;
};

type StateProps = {
  announcement?: Announcement;
  deletingAnnouncement: boolean;
};

type Props = {
  announcementId?: string;
  isInnerModal?: boolean;
  onCancel: () => void;
  onClosed?: () => void;
  onDeleted?: () => void;
  show?: boolean;
};

type State = {
  localAnnouncement?: Announcement;
};

class DeleteAnnouncementModal extends React.Component<Props & StateProps & DispatchProps, State> {
  readonly state: State = {
    // Keeps a copy of the announcement being deleted local to this modal component
    // When the deletion operation is confirmed, this allows the deleted announcement to be
    // removed from the redux store but kept locally until the confirmation modal is closed.
    // This prevents any content flash while the modal is transitioning out
    localAnnouncement: this.props.announcement,
  };

  componentDidUpdate(prevProps: Props & StateProps) {
    const { announcement } = this.props;
    if (announcement && prevProps.announcement !== announcement) {
      this.setState({ localAnnouncement: announcement });
    }
  }

  handleDelete = () => {
    const { deleteAnnouncement, onCancel, onClosed, onDeleted } = this.props;
    const { localAnnouncement } = this.state;
    localAnnouncement &&
      deleteAnnouncement &&
      deleteAnnouncement(localAnnouncement._id).then(() => {
        notification.success(formatMessage('AnnouncementsAdmin.DeleteModal.Success'));
        onCancel();
        onClosed && onClosed();
        onDeleted && onDeleted();
      });
  };

  render() {
    const { deletingAnnouncement, isInnerModal = false, onCancel, onClosed, show = false } = this.props;
    const { localAnnouncement } = this.state;

    if (!localAnnouncement) {
      return null;
    }

    return (
      <DestructiveModal
        open={show}
        onOpenChange={open => {
          if (!open) {
            onCancel();
            onClosed && onClosed();
          }
        }}
        title={formatMessage('AnnouncementsAdmin.DeleteModal.Title')}
        content={
          <>
            <FormattedMessage tagName="p" id="AnnouncementsAdmin.DeleteModal.Body" />
            <ul>
              <li>{formatLocalizedString(localAnnouncement.title)}</li>
            </ul>
          </>
        }
        confirmButtonText={formatMessage(`AnnouncementsAdmin.DeleteModal.${deletingAnnouncement ? 'ConfirmingButton' : 'ConfirmButton'}`)}
        cancelButtonText={formatMessage('Common.Cancel')}
        onConfirm={this.handleDelete}
        onCancel={onCancel}
        loading={deletingAnnouncement}
      />
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: Props): StateProps => ({
  announcement: state.announcementsAdmin.announcements.find(announcement => announcement._id === ownProps.announcementId),
  deletingAnnouncement: selectIsLoadingEvery(state, [DELETE_ANNOUNCEMENT.type]),
});

export default connect(mapStateToProps, {
  deleteAnnouncement,
})(DeleteAnnouncementModal);
